import { CrownFrame } from "@/components/CrownFrame";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, BadgeCheck, Swords, Map, Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { MwpBadge } from "@/components/MwpBadge";
import ShareWolf from "@/components/ShareWolf";

const POLY = [["c1", "Metaland Wolfpack Exclusive", "/art/c1_exclusive.webp"], ["c2", "Metaland Wolfpack (777)", "/art/frost.webp"], ["c3", "Project PUPS", "/art/c3_pups.webp"]];

export const WolfLink = ({ name, className = "", testid }) => (
  <Link to={`/wolf/${encodeURIComponent(name)}`} data-testid={testid} className={`hover:text-amber-200 hover:underline underline-offset-4 ${className}`}>{name}</Link>
);

function Look({ l }) {
  return (
    <div data-testid={`wolfpage-look-${l.id}`} className="rounded-2xl overflow-hidden border border-slate-700/70 bg-[#081820]/80">
      <div className="relative">
        <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image}`} alt={l.name} loading="lazy" className="w-full aspect-square object-cover" />
        <span data-testid={`wolfpage-look-tag-${l.id}`} className={`absolute top-2 left-2 text-[10px] font-mono-game px-2 py-1 rounded-full ${l.out_of_circulation ? "bg-red-600/90 text-white" : l.minted ? "bg-emerald-500/90 text-black" : "bg-slate-900/85 text-slate-200 border border-slate-600"}`}>
          {l.out_of_circulation ? "OUT OF CIRCULATION" : l.minted ? "MINTED NFT" : "NOT YET MINTED"}
        </span>
      </div>
      <div className="p-3 flex items-center justify-between gap-2">
        <p className="text-sm text-white truncate">#{l.no} · {l.name}</p>
        {l.minted && l.explorer && <a href={l.explorer} target="_blank" rel="noreferrer" data-testid={`wolfpage-look-view-${l.id}`} className="shrink-0 text-cyan-300 hover:text-cyan-200"><ExternalLink className="w-4 h-4" /></a>}
      </div>
    </div>
  );
}

export default function WolfPage() {
  const { name } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setD(null); setErr(false); api.get(`/wolves/${encodeURIComponent(name)}`).then(({ data }) => setD(data)).catch(() => setErr(true)); }, [name]);
  if (err) return <main className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="wolfpage-missing"><p className="font-display text-3xl text-white mb-3">No wolf by that name</p><Link to="/leaderboard" className="text-cyan-300 hover:underline">Back to the Leaderboard</Link></main>;
  if (!d) return <main className="max-w-6xl mx-auto px-6 py-24 text-slate-500">Loading…</main>;
  const minted = d.looks.filter((l) => l.minted).length;
  const poly = POLY.filter(([k]) => d.counts[k] > 0);
  return (
    <main className="max-w-6xl mx-auto px-6 py-14 space-y-10" data-testid="wolfpage">
      <section className="flex flex-col sm:flex-row gap-8 items-start">
        <CrownFrame tier={d.mwp_tier} className="rounded-3xl"><img src={d.portrait} alt={d.wolf_name} className="w-40 h-40 rounded-3xl object-cover border border-amber-400/50 nft-glow" data-testid="wolfpage-portrait" /></CrownFrame>
        <div>
          <p className="text-[11px] uppercase tracking-[0.35em] font-mono-game text-amber-300/80 mb-2">Wolf of the pack</p>
          <h1 data-testid="wolfpage-name" className="font-display text-4xl sm:text-5xl text-white">{d.wolf_name}</h1>
          {d.mwp_tier && <div className="mt-3"><MwpBadge tier={d.mwp_tier} big testid="wolfpage-mwp-badge" /></div>}
          {d.character && <p className="text-sm text-amber-200 mt-1">Playing as {d.character}</p>}
          <div className="flex flex-wrap gap-5 mt-4 text-sm text-slate-300">
            <span data-testid="wolfpage-level" className="inline-flex items-center gap-1.5"><Trophy className="w-4 h-4 text-amber-300" />Level {d.level}</span>
            <span data-testid="wolfpage-regions" className="inline-flex items-center gap-1.5"><Map className="w-4 h-4 text-cyan-300" />{d.regions_cleared} regions cleared</span>
            <span data-testid="wolfpage-wins" className="inline-flex items-center gap-1.5"><Swords className="w-4 h-4 text-fuchsia-300" />{d.wins} wins</span>
          </div>
          <ShareWolf d={d} />
        </div>
      </section>
      <section className="glass rounded-2xl p-6" data-testid="wolfpage-looks">
        <div className="flex flex-wrap items-end justify-between gap-2 mb-5">
          <h2 className="font-display text-2xl text-amber-100">1/1 wolves</h2>
          <p className="text-xs font-mono-game text-slate-400" data-testid="wolfpage-looks-count">{d.looks.length} owned · {minted} minted</p>
        </div>
        {d.looks.length ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">{d.looks.map((l) => <Look key={l.id} l={l} />)}</div>
          : <p className="text-sm text-slate-500">No 1/1 wolves yet.</p>}
      </section>
      <section className="glass rounded-2xl p-6" data-testid="wolfpage-polygon">
        <h2 className="font-display text-2xl text-amber-100 mb-5">Polygon NFTs</h2>
        {poly.length ? (
          <div className="grid sm:grid-cols-3 gap-4">
            {poly.map(([k, label, art]) => (
              <div key={k} data-testid={`wolfpage-poly-${k}`} className="flex items-center gap-4 rounded-xl border border-slate-700/70 bg-[#061016] p-3">
                <img src={art} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div><p className="text-sm text-slate-200">{label}</p><p className="font-mono-game text-xl text-amber-200" data-testid={`wolfpage-poly-${k}-count`}>{d.counts[k]}</p></div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-500">No Polygon NFTs linked.</p>}
      </section>
    </main>
  );
}
